import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler, getLoaderBtn, getModalSwitch, handleReloadShellConfig } from '@app/store/actions';
import { ModalActionsComponent, PasslyTextField } from '@app/components';
import { FormControl } from '@material-ui/core';
import { IUserData } from '@app/models';
import { store, onPressEnterModal, genericErrorString } from '@app/helpers';
import { vaultsService, authService } from '@app/services';

export interface IReceivedData {
    handleRefresh: Function;
}

export const AddIndividualVaultDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [displayName, setDisplayName] = useState('');
    const [displayNameInvalid, setDisplayNameInvalid] = useState(false);
    const [description, setDescription] = useState('');
    const [formValid, setFormValid] = useState(true);

    const currentUser: IUserData = store.getState().signIn.currentUser as IUserData;
    const displayNameRegex: string = '^[a-zA-Z 0-9\.\_\'\-]*$';

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        setDisplayName(currentUser.displayName + ' - ' + translation('INDIVIDUAL_VAULT'));
    }, []);

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'displayName':
                setDisplayName(value);
                break;
            case 'description':
                setDescription(value);
                break;
            default:
                break;
        }
    };

    const onDisplayNameChange = (e: any) => {
        setErrorMessage('');
        onFormChange(e);

        const nameChosen = e.target.value.trim();

        if (nameChosen === '') {
            setDisplayNameInvalid(true);
        }
        else {
            const re = new RegExp(displayNameRegex);

            if (!re.test(nameChosen)) {
                setDisplayNameInvalid(true);
                return;
            }

            setDisplayNameInvalid(false);
        }
    };

    useEffect(() => {
        if (displayNameInvalid) {
            setFormValid(false);
        }
        else {
            setFormValid(true);
        }
    }, [displayNameInvalid]);

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const handleSaveBtn = () => {
        dispatch(getLoaderBtn(true));

        // first check if vault name is available
        let jsonObject: any = {};

        jsonObject.displayName = displayName;
        jsonObject.tenantId = null;
        jsonObject.onBehalfOfOrgId = currentUser.oid;

        vaultsService.isVaultNameAvailable(jsonObject, handleError, handleSuccessVaultName);
    };

    const handleSuccessVaultName = (response: any) => {
        const value = response.data.value;

        if (value === true) {
            // save vault
            let jsonObject: any = {};
            jsonObject.vault = {};
            jsonObject.vault.DisplayName = displayName;
            jsonObject.vault.Description = description;
            jsonObject.vault.OrganizationId = currentUser.oid;

            vaultsService.addIndividualVault(jsonObject, handleError, handleSuccess);
        }
        else {
            dispatch(getLoaderBtn(false));
            setErrorMessage(translation('INVALID_TAKEN_VAULT_NAME'));
        }
    };

    const handleSuccess = () => {
        // this callback is sent to the getNewAccessToken function and only called after the getNewAccessToken function is finished.
        const callback = () => {
            dispatch(getLoaderBtn(false));
            dispatch(getModalSwitch(false));
            dispatch(handleReloadShellConfig());
            props.handleRefresh(true, true);
        };

        authService.getNewAccessToken(callback);
    };

    const handleError = (error: any) => {
        setErrorMessage(genericErrorString(error));

        dispatch(getLoaderBtn(false));
    };

    return (
        <>
        <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSaveBtn, !formValid)}>
            <div className='modal__medium flex1'>
                <div>
                    <div className='modal__body--row'>
                        <p>{translation('INDIVIDUAL_VAULT_DESC')}</p>
                    </div>
                    <form method='post'>
                        <div className='modal__body--row'>
                            <FormControl>
                                <PasslyTextField
                                    name='displayName'
                                    label={'DISPLAY_NAME'}
                                    value={displayName}
                                    onChange={onDisplayNameChange}
                                    id='displayName-input'
                                    inputProps={{ maxLength: 100 }}
                                    autoFocus
                                    required
                                    error={displayNameInvalid}
                                    helperText={displayNameInvalid && translation('REQUIRED_FIELD')}
                                    fullWidth
                                />
                            </FormControl>
                        </div>
                        <div className='modal__body--row'><br />
                            <FormControl>
                                <PasslyTextField
                                    name='description'
                                    label={'DESCRIPTION'}
                                    value={description}
                                    onChange={onFormChange}
                                    id='description-input'
                                    inputProps={{ maxLength: 500 }}
                                />
                            </FormControl>
                        </div>
                    </form>
                </div>
            </div>

            {
                errorMessage &&
                <div className='modal__error'>{errorMessage}</div>
            }
        </div>
            <ModalActionsComponent
                primaryBtn={translation('ADD_VAULT')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={!formValid}
                id='addVault'
            />
        </>
    );
};